/** @jsx jsx */
import { jsx } from '@emotion/react';
import { useContext, useMemo, useState, useEffect } from 'react';
import { WorkspaceContext } from '/views/workspace/workspaceContext';
import { selectDilDefinitionByName } from 'srs-middleware';
import { useFacetsState } from 'hooks/useFacetsState';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { FacetDetail } from '/views/filterHub/facet/facetDetail';
import { facets } from 'widgets/endpoints';
import { FacetGroup } from '../../../components/facets/facetGroup';
import { ExcludedFacetsDisplay } from '../../../components/facets/facetItem';
import { facetPreviewCount } from '../facetVars';
import { SORT_TYPES } from 'components/sortSelect';
import { getReadOnlyOverride } from 'utilities/readOnlyOverrides';
import { EmptyFacetsMessage } from 'components/facets/emptyFacetsMessage';

/**
 * This component displays a smaller set of Facets that appears with the other property filters
 *
 * @param {DILProperty} property property to render
 * @param {function} openNewPanel passed down from blueprint PanelStack component. Opens a detailed facet component
 * @param {function} toggleCollapse callback to open/collapse the component
 * @param {boolean} isOpen is the component open or is it collapsed
 */
export const FacetPreview = ({ property, openNewPanel, toggleCollapse, isOpen = false }) => {
	const [filters, setFilters] = useState(null);
	const { getTextFilter, getActiveFacets, toggleExcludeFacet, updateFacets } = useFacetsState();
	const textFilter = getTextFilter();
	const {
		state: {
			explorer: { selectedDefinitionName },
			transient: { activeResult, rootResult },
		},
	} = useContext(WorkspaceContext);

	const activeFacetCount = Object.values(getActiveFacets()?.[property.name] || {}).length;
	const totalFacetCount = filters?.[property.name]?.fullSize;

	// get the actual definition
	const selectedDefinition = useMemo(() => {
		return selectDilDefinitionByName(selectedDefinitionName);
	}, [selectedDefinitionName]);

	// fetch the data, given the current parameters
	// if on a "child" result
	// {key: "Label", active: 87, total: 136}
	// if on "root" result
	// {key: "Label", active: 136, total: 0}
	const { data, loading } = useAsyncCallback(async () => {
		if (activeResult && property && selectedDefinition && rootResult) {
			if (activeResult.count > 0) {
				const sortingTypeByProperty = getReadOnlyOverride('defaultFacetSortOrder');
				const sort = SORT_TYPES?.[sortingTypeByProperty?.[property.name]]?.value;
				const parameters = {
					maxValues: facetPreviewCount,
					textFilter: textFilter,
					sort: sort ?? null,
					parentResultId: rootResult.resultId
				};

				//If the root result is a filter of another result, it will have a parentId. If parentId is included
				// in the object sent to SRS, then the counts will include records from the parent query. We don't want this. So:
				//If the active result is the same as the root, then remove parentId.
				const ignoreParentId = activeResult.resultId === rootResult.resultId;
				const response = await facets(
					activeResult,
					selectedDefinitionName,
					{
						propertyList: [property.name],
						parameters: parameters,
					},
					ignoreParentId
				);

				const filters = response[selectedDefinition.systemDefinitionId];
				return { filters };
			}
		}
	}, [activeResult, property, textFilter, selectedDefinition, rootResult]);

	// pull out the filters from the return value of the async callback
	// need to keep using the old filters until we new filters so the screen doesn't blink to blank during loading
	useEffect(() => {
		if (!loading && data) {
			// SRS keys are case insensitive which can cause some problems, transform key to lowercase
			Object.values(data.filters).forEach((values) => {
				values.entries.forEach((entry) => {
					entry.label = entry.key;
					entry.key = entry.key.toLowerCase();
				});
			});
			setFilters(data.filters);
		}
	}, [data, loading]);

	return (
		<FacetGroup
			onResetClick={() => {
				updateFacets(property.name, []);
			}}
			{...{
				property,
				openNewPanel,
				toggleCollapse,
				isOpen,
				loading,
				activeFacetCount,
				totalFacetCount,
			}}
		>
			<ExcludedFacetsDisplay
				excludedFacets={getActiveFacets()}
				toggleFacet={toggleExcludeFacet}
				property={property.name}
			/>

			{filters?.[property.name]?.entries.slice(0, facetPreviewCount).map((facet) => {
				return (
					<FacetDetail
						key={facet.key}
						facetKey={facet.key}
						propertyName={property.name}
						facetName={facet.label}
						facetActiveValue={facet.count}
						facetTotalValue={facet?.reference}
					/>
				);
			})}

			{filters?.[property.name]?.entries.length === 0 && <EmptyFacetsMessage />}
		</FacetGroup>
	);
};
